import React, { useEffect, useMemo, useState } from "react"
import {
  Button,
  Col,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
} from "reactstrap"
import Select from "react-select"
import { useDispatch } from "react-redux"
import { useTranslation } from "react-i18next"
import { assignOrdersToRider } from "store/actions/orderAction"
import { showAlert } from "store/reducers/alertReducer"
import { post } from "helpers/api_helper"
import { RIDERS_FOR_ASSIGNMENT } from "helpers/url_helper"
import { getRiderAvailabilityDisplay } from "utils/riderAvailability"

const AssignToRiderModal = ({
  isOpen,
  toggle,
  selectedOrderIds = [],
  selectedOrders = [],
  branchsList = [],
  onSuccess,
}) => {
  const { t, i18n: { language } } = useTranslation()
  const dispatch = useDispatch()

  const defaultBranchId = useMemo(() => {
    const first = selectedOrders.find(o => o.branchId)
    if (!first?.branchId) return ""
    return typeof first.branchId === "object"
      ? first.branchId._id
      : first.branchId
  }, [selectedOrders])

  const [branchFilter, setBranchFilter] = useState(null)
  const [availabilityFilter, setAvailabilityFilter] = useState(null)
  const [riders, setRiders] = useState([])
  const [loadingRiders, setLoadingRiders] = useState(false)
  const [selectedRider, setSelectedRider] = useState(null)
  const [submitting, setSubmitting] = useState(false)

  const branchOptions = useMemo(
    () => [
      { label: t("All Riders"), value: "all" },
      ...branchsList.map(branch => ({
        label: language === "sa" ? branch.nameAr : branch.name,
        value: branch._id,
      })),
    ],
    [branchsList, language, t]
  )

  const availabilityOptions = useMemo(
    () => [
      { label: t("All"), value: "all" },
      { label: t("Available"), value: "available" },
      { label: t("Not Available"), value: "unavailable" },
      { label: t("On Delivery"), value: "on_delivery" },
    ],
    [t]
  )

  useEffect(() => {
    if (!isOpen) return

    const defaultOption =
      branchOptions.find(o => o.value === defaultBranchId) ||
      branchOptions.find(o => o.value === "all") ||
      null
    setBranchFilter(defaultOption)
    setAvailabilityFilter(availabilityOptions[0])
    setSelectedRider(null)
  }, [isOpen, defaultBranchId, branchOptions, availabilityOptions])

  useEffect(() => {
    if (!isOpen || !branchFilter || !availabilityFilter) return

    const fetchRiders = async () => {
      setLoadingRiders(true)
      try {
        const response = await post(RIDERS_FOR_ASSIGNMENT, {
          branchId: branchFilter.value,
          availabilityFilter: availabilityFilter.value,
        })
        setRiders(response?.data || [])
      } catch {
        setRiders([])
      } finally {
        setLoadingRiders(false)
      }
    }

    fetchRiders()
  }, [isOpen, branchFilter, availabilityFilter])

  const handleAssign = () => {
    if (!selectedRider) {
      dispatch(
        showAlert({ text: t("Please select a rider"), type: "error" })
      )
      return
    }

    setSubmitting(true)
    dispatch(
      assignOrdersToRider({
        orderIds: selectedOrderIds,
        partnerId: selectedRider._id,
      })
    )
      .then(action => {
        const { statusCode, status, msg } = action?.payload || {}
        if (statusCode === 200 && status) {
          dispatch(showAlert({ text: msg, type: "success" }))
          onSuccess?.()
          toggle()
        } else {
          dispatch(showAlert({ text: msg || t("Something went wrong"), type: "error" }))
        }
      })
      .finally(() => setSubmitting(false))
  }

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg" centered>
      <ModalHeader toggle={toggle}>{t("Assign to Rider")}</ModalHeader>
      <ModalBody>
        <p className="text-muted mb-3">
          {t("{{count}} order(s) selected", { count: selectedOrderIds.length })}
        </p>

        <div className="mb-4">
          <label className="form-label">{t("Filter by Branch")}</label>
          <Select
            value={branchFilter}
            options={branchOptions}
            onChange={setBranchFilter}
            placeholder={t("Select Branch")}
          />
        </div>

        <div className="mb-4">
          <label className="form-label">{t("Filter by Availability")}</label>
          <Select
            value={availabilityFilter}
            options={availabilityOptions}
            onChange={setAvailabilityFilter}
            placeholder={t("Select Availability")}
          />
        </div>

        {loadingRiders ? (
          <div className="text-center py-4">
            <Spinner size="sm" /> {t("Loading riders...")}
          </div>
        ) : riders.length === 0 ? (
          <p className="text-center text-muted py-3">{t("No riders found")}</p>
        ) : (
          <Row className="g-3">
            {riders.map(rider => {
              const isSelected = selectedRider?._id === rider._id
              const availability = getRiderAvailabilityDisplay(rider, t)
              return (
                <Col md={6} key={rider._id}>
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => setSelectedRider(rider)}
                    onKeyDown={e => e.key === "Enter" && setSelectedRider(rider)}
                    className={`border rounded p-3 h-100 ${
                      isSelected ? "border-primary bg-light" : ""
                    }`}
                    style={{ cursor: "pointer" }}
                  >
                    <div className="d-flex align-items-center gap-3">
                      {rider.profilePhoto ? (
                        <img
                          src={rider.profilePhoto}
                          alt={rider.name}
                          className="rounded-circle"
                          width={48}
                          height={48}
                          style={{ objectFit: "cover" }}
                        />
                      ) : (
                        <div
                          className="rounded-circle bg-secondary text-white d-flex align-items-center justify-content-center"
                          style={{ width: 48, height: 48, fontSize: 18 }}
                        >
                          {(rider.name || "?").charAt(0).toUpperCase()}
                        </div>
                      )}
                      <div className="flex-grow-1">
                        <div className="d-flex align-items-center gap-2">
                          <span
                            className="rounded-circle flex-shrink-0"
                            style={{
                              width: 10,
                              height: 10,
                              backgroundColor: availability.color,
                            }}
                            title={availability.label}
                          />
                          <div className="fw-semibold">{rider.name}</div>
                        </div>
                        <div className="small ms-3">
                          <span className={availability.textClass}>
                            {availability.riderLabel}
                          </span>
                        </div>
                        <div className="small text-muted">{rider.email || "-"}</div>
                        <div className="small">
                          {rider.phone ? (
                            <span
                              dir="ltr"
                              style={{ unicodeBidi: "bidi-override" }}
                            >
                              {rider.phone}
                            </span>
                          ) : (
                            "-"
                          )}
                        </div>
                        <div className="small text-muted mt-1">
                          {language === "sa"
                            ? rider.branchNameAr || rider.branchName || t("All Branches")
                            : rider.branchName || t("All Branches")}
                        </div>
                      </div>
                    </div>
                  </div>
                </Col>
              )
            })}
          </Row>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={toggle} disabled={submitting}>
          {t("Cancel")}
        </Button>
        <Button color="primary" onClick={handleAssign} disabled={submitting || !selectedRider}>
          {submitting ? <Spinner size="sm" /> : t("Assign")}
        </Button>
      </ModalFooter>
    </Modal>
  )
}

export default AssignToRiderModal
